import { Observable, Observer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

/**
 * The duration in milliseconds (ms) of an animation.
 */
export enum Duration {
    Xs = 100,
    Sm = 167,
    Md = 267,
    Lg = 367,
    Xl = 467
}

/**
 * The options for animating an element.
 */
export interface AnimationOptions {
    /**
     * The animation CSS class(es) for animating an element.
     */
    animationClass: string | string[];

    /**
     * The duration of an animation in milliseconds (ms).
     */
    duration: Duration;

    /**
     * Whether to remove the animation CSS class(es) after the animation has completed.
     */
    removeClass?: boolean;
}

/**
 * The available animation CSS classes and associated durations in milliseconds (MS).
 */
export const animations = {
    slide: {
        in: {
            right: {
                xs: <AnimationOptions>{ animationClass: 'sme-animation-slide-in-right-xs', duration: Duration.Xs },
                sm: <AnimationOptions>{ animationClass: 'sme-animation-slide-in-right-sm', duration: Duration.Sm },
                md: <AnimationOptions>{ animationClass: 'sme-animation-slide-in-right-md', duration: Duration.Md },
                xl: <AnimationOptions>{ animationClass: 'sme-animation-slide-in-right-xl', duration: Duration.Xl }
            },
            left: {
                xs: <AnimationOptions>{ animationClass: 'sme-animation-slide-in-left-xs', duration: Duration.Xs },
                sm: <AnimationOptions>{ animationClass: 'sme-animation-slide-in-left-sm', duration: Duration.Sm },
                md: <AnimationOptions>{ animationClass: 'sme-animation-slide-in-left-md', duration: Duration.Md },
                xl: <AnimationOptions>{ animationClass: 'sme-animation-slide-in-left-xl', duration: Duration.Xl }
            },
            up: {
                xs: <AnimationOptions>{ animationClass: 'sme-animation-slide-in-up-xs', duration: Duration.Xs },
                sm: <AnimationOptions>{ animationClass: 'sme-animation-slide-in-up-sm', duration: Duration.Sm },
                md: <AnimationOptions>{ animationClass: 'sme-animation-slide-in-up-md', duration: Duration.Md },
                xl: <AnimationOptions>{ animationClass: 'sme-animation-slide-in-up-xl', duration: Duration.Xl }
            },
            down: {
                xs: <AnimationOptions>{ animationClass: 'sme-animation-slide-in-down-xs', duration: Duration.Xs },
                sm: <AnimationOptions>{ animationClass: 'sme-animation-slide-in-down-sm', duration: Duration.Sm },
                md: <AnimationOptions>{ animationClass: 'sme-animation-slide-in-down-md', duration: Duration.Md },
                xl: <AnimationOptions>{ animationClass: 'sme-animation-slide-in-down-xl', duration: Duration.Xl }
            }
        },
        out: {
            right: {
                xs: <AnimationOptions>{ animationClass: 'sme-animation-slide-out-right-xs', duration: Duration.Xs },
                sm: <AnimationOptions>{ animationClass: 'sme-animation-slide-out-right-sm', duration: Duration.Sm },
                md: <AnimationOptions>{ animationClass: 'sme-animation-slide-out-right-md', duration: Duration.Md },
                xl: <AnimationOptions>{ animationClass: 'sme-animation-slide-out-right-xl', duration: Duration.Xl }
            },
            left: {
                xs: <AnimationOptions>{ animationClass: 'sme-animation-slide-out-left-xs', duration: Duration.Xs },
                sm: <AnimationOptions>{ animationClass: 'sme-animation-slide-out-left-sm', duration: Duration.Sm },
                md: <AnimationOptions>{ animationClass: 'sme-animation-slide-out-left-md', duration: Duration.Md },
                xl: <AnimationOptions>{ animationClass: 'sme-animation-slide-out-left-xl', duration: Duration.Xl }
            },
            up: {
                xs: <AnimationOptions>{ animationClass: 'sme-animation-slide-out-up-xs', duration: Duration.Xs },
                sm: <AnimationOptions>{ animationClass: 'sme-animation-slide-out-up-sm', duration: Duration.Sm },
                md: <AnimationOptions>{ animationClass: 'sme-animation-slide-out-up-md', duration: Duration.Md },
                xl: <AnimationOptions>{ animationClass: 'sme-animation-slide-out-up-xl', duration: Duration.Xl }
            },
            down: {
                xs: <AnimationOptions>{ animationClass: 'sme-animation-slide-out-down-xs', duration: Duration.Xs },
                sm: <AnimationOptions>{ animationClass: 'sme-animation-slide-out-down-sm', duration: Duration.Sm },
                md: <AnimationOptions>{ animationClass: 'sme-animation-slide-out-down-md', duration: Duration.Md },
                xl: <AnimationOptions>{ animationClass: 'sme-animation-slide-out-down-xl', duration: Duration.Xl }
            }
        }
    },
    fade: {
        in: {
            xs: <AnimationOptions>{ animationClass: 'sme-animation-fade-in-xs', duration: Duration.Xs },
            sm: <AnimationOptions>{ animationClass: 'sme-animation-fade-in-sm', duration: Duration.Sm },
            md: <AnimationOptions>{ animationClass: 'sme-animation-fade-in-md', duration: Duration.Md },
            lg: <AnimationOptions>{ animationClass: 'sme-animation-fade-in-lg', duration: Duration.Lg },
            xl: <AnimationOptions>{ animationClass: 'sme-animation-fade-in-xl', duration: Duration.Xl }
        },
        out: {
            xs: <AnimationOptions>{ animationClass: 'sme-animation-fade-out-xs', duration: Duration.Xs },
            sm: <AnimationOptions>{ animationClass: 'sme-animation-fade-out-sm', duration: Duration.Sm },
            md: <AnimationOptions>{ animationClass: 'sme-animation-fade-out-md', duration: Duration.Md },
            lg: <AnimationOptions>{ animationClass: 'sme-animation-fade-out-lg', duration: Duration.Lg },
            xl: <AnimationOptions>{ animationClass: 'sme-animation-fade-out-xl', duration: Duration.Xl }
        }
    },
    scale: {
        in: {
            up: {
                sm: <AnimationOptions>{ animationClass: 'sme-animation-scale-in-up-sm', duration: Duration.Sm },
                neutral: <AnimationOptions>{ animationClass: 'sme-animation-scale-in-up-neutral', duration: Duration.Sm },
                lg: <AnimationOptions>{ animationClass: 'sme-animation-scale-in-up-lg', duration: Duration.Sm }
            },
            down: {
                sm: <AnimationOptions>{ animationClass: 'sme-animation-scale-in-down-sm' },
                neutral: <AnimationOptions>{ animationClass: 'sme-animation-scale-in-down-neutral' },
                lg: <AnimationOptions>{ animationClass: 'sme-animation-scale-in-down-lg' }
            }
        },
        out: {
            up: {
                sm: <AnimationOptions>{ animationClass: 'sme-animation-scale-out-up-sm' },
                neutral: <AnimationOptions>{ animationClass: 'sme-animation-scale-out-up-neutral' },
                lg: <AnimationOptions>{ animationClass: 'sme-animation-scale-out-up-lg' }
            },
            down: {
                sm: <AnimationOptions>{ animationClass: 'sme-animation-scale-out-down-sm' },
                neutral: <AnimationOptions>{ animationClass: 'sme-animation-scale-out-down-neutral' },
                lg: <AnimationOptions>{ animationClass: 'sme-animation-scale-out-down-lg' }
            }
        }
    },
    transition: {
        height: {
            xs: <AnimationOptions>{ animationClass: 'sme-animation-transition-height-xs', duration: Duration.Xs },
            sm: <AnimationOptions>{ animationClass: 'sme-animation-transition-height-sm', duration: Duration.Sm },
            md: <AnimationOptions>{ animationClass: 'sme-animation-transition-height-md', duration: Duration.Md },
            lg: <AnimationOptions>{ animationClass: 'sme-animation-transition-height-lg', duration: Duration.Lg },
            xl: <AnimationOptions>{ animationClass: 'sme-animation-transition-height-xl', duration: Duration.Xl }
        },
        width: {
            xs: <AnimationOptions>{ animationClass: 'sme-animation-transition-width-xs', duration: Duration.Xs },
            sm: <AnimationOptions>{ animationClass: 'sme-animation-transition-width-sm', duration: Duration.Sm },
            md: <AnimationOptions>{ animationClass: 'sme-animation-transition-width-md', duration: Duration.Md },
            lg: <AnimationOptions>{ animationClass: 'sme-animation-transition-width-lg', duration: Duration.Lg },
            xl: <AnimationOptions>{ animationClass: 'sme-animation-transition-width-xl', duration: Duration.Xl }
        }
    },
    rotate: {
        left: {
            md: <AnimationOptions>{ animationClass: 'sme-animation-rotate-left-md', duration: Duration.Md }
        },
        right: {
            md: <AnimationOptions>{ animationClass: 'sme-animation-rotate-right-md', duration: Duration.Md }
        }
    }
};

/**
 * A utility class for animating HTML elements.
 */
export class Animator {
    /**
     * Animates a HTML element.
     *
     * @param element The HTML element to animate.
     * @param options The options to animate the element with.
     * @returns A void observable for the animation event.
     */
    public static animate(element: HTMLElement, ...options: AnimationOptions[]): Observable<void> {
        if (MsftSme.isNullOrUndefined(options) || options.length === 0) {
            throw new Error(`animate(...) called without an 'options' parameter.`);
        }

        const currentOptions = options.splice(0)[0];
        const classes = Array.isArray(currentOptions.animationClass) ? currentOptions.animationClass : [currentOptions.animationClass];
        element.classList.add(...classes);

        const currentObservable = new Observable(
            (observer: Observer<void>) => {
                const timeout = setTimeout(
                    () => {
                        if (currentOptions.removeClass) {
                            element.classList.remove(...classes);
                        }

                        observer.next(null);
                        observer.complete();
                    },
                    currentOptions.duration);

                return () => clearTimeout(timeout);
            }
        );

        if (options.length > 0) {
            return currentObservable.pipe(mergeMap(() => this.animate(element, ...options)));
        }

        return currentObservable;
    }

    /**
     * Animates a HTML element to slide in to the left.
     *
     * @param element The HTML element to animate.
     * @param duration The duration of the animation.
     * @param removeClass Whether to remove the animation CSS class from the element once the animation completes.
     * @returns A void observable for the animation event.
     */
    public static slideInToLeft(element: HTMLElement, duration: Duration, removeClass: boolean = false): Observable<void> {
        switch (duration) {
            case Duration.Xs:
                return this.animate(element, { ...animations.slide.in.left.xs, removeClass: removeClass });
            case Duration.Sm:
                return this.animate(element, { ...animations.slide.in.left.sm, removeClass: removeClass });
            case Duration.Md:
                return this.animate(element, { ...animations.slide.in.left.md, removeClass: removeClass });
            case Duration.Xl:
                return this.animate(element, { ...animations.slide.in.left.xl, removeClass: removeClass });
            default:
                return this.animate(element, { ...animations.slide.in.left.md, removeClass: removeClass });
        }
    }

    /**
     * Animates a HTML element to slide in to the right.
     *
     * @param element The HTML element to animate.
     * @param duration The duration of the animation.
     * @param removeClass Whether to remove the animation CSS class from the element once the animation completes.
     * @returns A void observable for the animation event.
     */
    public static slideInToRight(element: HTMLElement, duration: Duration, removeClass: boolean = false): Observable<void> {
        switch (duration) {
            case Duration.Xs:
                return this.animate(element, { ...animations.slide.in.right.xs, removeClass: removeClass });
            case Duration.Sm:
                return this.animate(element, { ...animations.slide.in.right.sm, removeClass: removeClass });
            case Duration.Md:
                return this.animate(element, { ...animations.slide.in.right.md, removeClass: removeClass });
            case Duration.Xl:
                return this.animate(element, { ...animations.slide.in.right.xl, removeClass: removeClass });
            default:
                return this.animate(element, { ...animations.slide.in.right.md, removeClass: removeClass });
        }
    }

    /**
     * Animates a HTML element to slide out to the left.
     *
     * @param element The HTML element to animate.
     * @param duration The duration of the animation.
     * @param removeClass Whether to remove the animation CSS class from the element once the animation completes.
     * @returns A void observable for the animation event.
     */
    public static slideOutToLeft(element: HTMLElement, duration: Duration, removeClass: boolean = false): Observable<void> {
        switch (duration) {
            case Duration.Xs:
                return this.animate(element, { ...animations.slide.out.left.xs, removeClass: removeClass });
            case Duration.Sm:
                return this.animate(element, { ...animations.slide.out.left.sm, removeClass: removeClass });
            case Duration.Md:
                return this.animate(element, { ...animations.slide.out.left.md, removeClass: removeClass });
            case Duration.Xl:
                return this.animate(element, { ...animations.slide.out.left.xl, removeClass: removeClass });
            default:
                return this.animate(element, { ...animations.slide.out.left.md, removeClass: removeClass });
        }
    }

    /**
     * Animates a HTML element to slide out to the right.
     *
     * @param element The HTML element to animate.
     * @param duration The duration of the animation.
     * @param removeClass Whether to remove the animation CSS class from the element once the animation completes.
     * @returns A void observable for the animation event.
     */
    public static slideOutToRight(element: HTMLElement, duration: Duration, removeClass: boolean = false): Observable<void> {
        switch (duration) {
            case Duration.Xs:
                return this.animate(element, { ...animations.slide.out.right.xs, removeClass: removeClass });
            case Duration.Sm:
                return this.animate(element, { ...animations.slide.out.right.sm, removeClass: removeClass });
            case Duration.Md:
                return this.animate(element, { ...animations.slide.out.right.md, removeClass: removeClass });
            case Duration.Xl:
                return this.animate(element, { ...animations.slide.out.right.xl, removeClass: removeClass });
            default:
                return this.animate(element, { ...animations.slide.out.right.md, removeClass: removeClass });
        }
    }
}
